import Spinner from "components/Spinner";
import { setNotification } from "features/Notification/notificationSlice";
import { useCallback, useEffect, useMemo, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { useDispatch } from "react-redux";
import authHeader from "services/auth-header";
import API from "services/axios";

/**
 * Size Sets -> Group Sets (user, 2026-10-27). A set is made by picking sizes
 * from All Sizes; sets have their own order (sortId) and each size has its
 * order inside its set. Left: the sets. Right: the chosen set's sizes.
 *
 * WO product Add/Edit offers the sets as "Add Size Set". (The Order Sheet
 * uses All Sizes, not the sets.)
 */
export default function SizeGroupSets() {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [sets, setSets] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [newName, setNewName] = useState("");
  const [editing, setEditing] = useState(null); // { id, name }
  const [allSizes, setAllSizes] = useState([]);
  const [showPicker, setShowPicker] = useState(false);
  const [picked, setPicked] = useState([]);
  const [pickerSearch, setPickerSearch] = useState("");

  const notify = (message, type = "success") =>
    dispatch(setNotification({ message, type }));

  // Runs an API call with the spinner, reports a failure, reloads on success.
  const run = async (fn, { reload = true } = {}) => {
    try {
      setLoading(true);
      const { data } = await fn();
      if (data?.success === false) throw new Error(data?.message || "Failed");
      if (data?.message) notify(data.message);
      if (reload) await getSets();
      return data;
    } catch (err) {
      notify(err?.response?.data?.message || err.message, "error");
      return null;
    } finally {
      setLoading(false);
    }
  };

  const getSets = useCallback(async () => {
    const { data } = await API.post("/sales/size-sets", {}, { headers: authHeader() });
    const list = data?.data || [];
    setSets(list);
    setSelectedId((cur) =>
      list.some((s) => s.id === cur) ? cur : list[0]?.id ?? null,
    );
  }, []);

  useEffect(() => {
    setLoading(true);
    getSets()
      .catch((err) => notify(err.message, "error"))
      .finally(() => setLoading(false));
    API.post(
      "sales/products/sizes",
      { page: 1, limit: 999 },
      { headers: authHeader() },
    )
      .then(({ data }) => setAllSizes(data?.data || data?.result || []))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selected = sets.find((s) => s.id === selectedId) || null;
  const inSet = useMemo(
    () => new Set((selected?.items || []).map((i) => i.sizeId)),
    [selected],
  );
  const pickable = allSizes
    .filter((s) => !inSet.has(s.id))
    .filter((s) =>
      String(s.name || "").toLowerCase().includes(pickerSearch.trim().toLowerCase()),
    );

  const addSet = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    const data = await run(() =>
      API.post("/sales/size-set", { name: newName }, { headers: authHeader() }),
    );
    if (data?.data?.id) {
      setNewName("");
      setSelectedId(data.data.id);
    }
  };

  const saveName = async () => {
    if (!editing?.name?.trim()) return;
    await run(() =>
      API.patch("/sales/size-set", editing, { headers: authHeader() }),
    );
    setEditing(null);
  };

  const deleteSet = (set) => {
    if (!window.confirm(`Delete the size set "${set.name}"?`)) return;
    run(() => API.delete(`/sales/size-set/${set.id}`, { headers: authHeader() }));
  };

  const moveSet = (id, direction) =>
    run(() =>
      API.post("/sales/size-set/move", { id, direction }, { headers: authHeader() }),
    );

  const moveItem = (id, direction) =>
    run(() =>
      API.post(
        "/sales/size-set-item/move",
        { id, direction },
        { headers: authHeader() },
      ),
    );

  const removeItem = (id) =>
    run(() => API.delete(`/sales/size-set-item/${id}`, { headers: authHeader() }));

  const addPicked = async () => {
    if (!selected || !picked.length) return;
    // In All Sizes order, so a set reads in the usual size order.
    const order = new Map(allSizes.map((s, i) => [s.id, i]));
    const sizeIds = [...picked].sort((a, b) => order.get(a) - order.get(b));
    await run(() =>
      API.post(
        "/sales/size-set/items",
        { sizeSetId: selected.id, sizeIds },
        { headers: authHeader() },
      ),
    );
    setPicked([]);
    setShowPicker(false);
  };

  const arrow = (onClick, disabled, icon, title) => (
    <button
      type="button"
      className="btn btn-sm btn-link p-0 px-1"
      title={title}
      disabled={disabled || loading}
      onClick={onClick}
    >
      <i className={`mdi ${icon}`}></i>
    </button>
  );

  return (
    <div className="row g-3">
      {loading && <Spinner />}

      {/* Sets */}
      <div className="col-md-5">
        <div className="bg-white custom-shadow-sm custom-border-radius p-3 h-100">
          <h5 className="fw-bold mb-2">Size Sets</h5>
          <form className="d-flex gap-2 mb-3" onSubmit={addSet}>
            <input
              className="form-control form-control-sm"
              placeholder="New set name, e.g. Youth"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
            />
            <button className="btn btn-sm btn-primary text-nowrap" disabled={!newName.trim()}>
              <i className="mdi mdi-plus me-1"></i>Add Set
            </button>
          </form>
          {!sets.length && <div className="text-muted small">No size sets yet.</div>}
          <ul className="list-group">
            {sets.map((set, i) => (
              // Selected: a light tint and a primary left edge, not Bootstrap's
              // solid "active" (it washed out the icons -- user, 2026-10-27).
              <li
                key={set.id}
                className={`list-group-item d-flex align-items-center gap-2 py-1 ${
                  set.id === selectedId ? "fw-bold text-primary" : ""
                }`}
                style={{
                  cursor: "pointer",
                  background: set.id === selectedId ? "#eef0ff" : undefined,
                  boxShadow:
                    set.id === selectedId ? "inset 3px 0 0 var(--bs-primary)" : undefined,
                }}
                onClick={() => setSelectedId(set.id)}
              >
                <span className="text-muted small" style={{ width: 22 }}>
                  {i + 1}
                </span>
                {editing?.id === set.id ? (
                  <input
                    autoFocus
                    className="form-control form-control-sm"
                    value={editing.name}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") saveName();
                      if (e.key === "Escape") setEditing(null);
                    }}
                    onBlur={saveName}
                  />
                ) : (
                  <span className="flex-grow-1">
                    {set.name}{" "}
                    <span className="small opacity-75">
                      ({set.items?.length || 0})
                    </span>
                  </span>
                )}
                <span className="ms-auto text-nowrap" onClick={(e) => e.stopPropagation()}>
                  {arrow(() => moveSet(set.id, "up"), i === 0, "mdi-arrow-up", "Move up")}
                  {arrow(
                    () => moveSet(set.id, "down"),
                    i === sets.length - 1,
                    "mdi-arrow-down",
                    "Move down",
                  )}
                  {arrow(
                    () => setEditing({ id: set.id, name: set.name || "" }),
                    false,
                    "mdi-pencil",
                    "Rename",
                  )}
                  {arrow(() => deleteSet(set), false, "mdi-delete text-danger", "Delete")}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* The set's sizes */}
      <div className="col-md-7">
        <div className="bg-white custom-shadow-sm custom-border-radius p-3 h-100">
          <div className="d-flex align-items-center mb-2">
            <h5 className="fw-bold mb-0">
              {selected ? `Sizes in ${selected.name}` : "Sizes"}
            </h5>
            {selected && (
              <button
                type="button"
                className="btn btn-sm btn-primary ms-auto"
                onClick={() => {
                  setPicked([]);
                  setPickerSearch("");
                  setShowPicker(true);
                }}
              >
                <i className="mdi mdi-plus me-1"></i>Add Sizes
              </button>
            )}
          </div>
          {!selected && <div className="text-muted small">Add a set on the left.</div>}
          {selected && !selected.items?.length && (
            <div className="text-muted small">
              No sizes yet -- "Add Sizes" picks them from All Sizes.
            </div>
          )}
          {selected?.items?.length > 0 && (
            <table className="table table-sm align-middle mb-0">
              <thead>
                <tr>
                  <th style={{ width: 70 }}>Sort</th>
                  <th>Size</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {selected.items.map((item, i) => (
                  <tr key={item.id}>
                    <td>{i + 1}</td>
                    <td className="fw-bold">{item.size?.name || "(deleted size)"}</td>
                    <td className="text-end text-nowrap">
                      {arrow(() => moveItem(item.id, "up"), i === 0, "mdi-arrow-up", "Move up")}
                      {arrow(
                        () => moveItem(item.id, "down"),
                        i === selected.items.length - 1,
                        "mdi-arrow-down",
                        "Move down",
                      )}
                      {arrow(
                        () => removeItem(item.id),
                        false,
                        "mdi-close text-danger",
                        "Remove from set",
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <Modal show={showPicker} onHide={() => setShowPicker(false)}>
        <Modal.Body className="bg-white" style={{ borderRadius: 15 }}>
          <h4 className="mb-2">Add Sizes to {selected?.name}</h4>
          <input
            className="form-control form-control-sm mb-2"
            placeholder="Search sizes"
            value={pickerSearch}
            onChange={(e) => setPickerSearch(e.target.value)}
          />
          <div className="d-flex flex-wrap gap-2" style={{ maxHeight: 360, overflowY: "auto" }}>
            {pickable.map((s) => {
              const on = picked.includes(s.id);
              return (
                <button
                  key={s.id}
                  type="button"
                  className={`btn btn-sm ${on ? "btn-primary" : "btn-outline-primary"}`}
                  onClick={() =>
                    setPicked((p) => (on ? p.filter((x) => x !== s.id) : [...p, s.id]))
                  }
                >
                  {s.name}
                </button>
              );
            })}
            {!pickable.length && (
              <div className="text-muted small">Every size is already in this set.</div>
            )}
          </div>
          <div className="d-flex justify-content-end gap-1 mt-3">
            <button className="btn btn-secondary" onClick={() => setShowPicker(false)}>
              Cancel
            </button>
            <button className="btn btn-primary" disabled={!picked.length} onClick={addPicked}>
              Add {picked.length || ""} Size{picked.length === 1 ? "" : "s"}
            </button>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
}
